import React from 'react';
import { Alert, Box, Chip, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import ChatWindow from '../ChatWindow';
import { chatStatusLabel, isActiveChatStatus } from '../../../lib/chatSessionStatus';
import ChatStageComposer from './ChatStageComposer';

/**
 * Center column — conversation stage with message feed and enterprise composer.
 */
export default function ChatStageColumn({
  selectedSessionId = '',
  selectedSession = null,
  sessionLoading = false,
  isReadOnlyView = false,
  canReply = false,
  messages = [],
  messagesLoading = false,
  replyText = '',
  onReplyChange,
  onSendReply,
  sending = false,
  markingSession = false,
  actionError = '',
  visitorTyping = false,
  sessionLabel,
  quickReplies = [],
  onQuickReply,
  onMarkSession,
}) {
  return (
    <Paper
      variant="outlined"
      square
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        height: '100%',
        borderRadius: 0,
        borderTop: 0,
        borderBottom: 0,
        borderLeft: 0,
        borderRight: 0,
        overflow: 'hidden',
      }}
    >
      {!selectedSessionId ? (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 4,
            bgcolor: 'grey.50',
          }}
        >
          <Typography variant="body2" color="text.secondary" textAlign="center">
            Select a session from the queue to open the conversation stage.
          </Typography>
        </Box>
      ) : sessionLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
          <CircularProgress size={28} />
        </Box>
      ) : !selectedSession ? (
        <Box sx={{ p: 3 }}>
          <Alert severity="warning">Could not load the selected session.</Alert>
        </Box>
      ) : (
        <>
          <Box
            sx={{
              px: 2,
              py: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              borderBottom: 1,
              borderColor: 'divider',
              bgcolor: 'background.paper',
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }} noWrap>
                {sessionLabel(selectedSession)}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
                <Chip
                  size="small"
                  label={chatStatusLabel(selectedSession.status)}
                  color={isActiveChatStatus(selectedSession.status) ? 'primary' : 'default'}
                  variant="outlined"
                />
                {isReadOnlyView ? <Chip size="small" label="Read-only" variant="outlined" /> : null}
              </Stack>
            </Box>
          </Box>

          <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <ChatWindow
              messages={messages}
              loading={messagesLoading}
              variant="admin"
              emptyMessage="No messages in this session yet."
            />

            <ChatStageComposer
              canReply={canReply}
              replyText={replyText}
              onReplyChange={onReplyChange}
              onSendReply={onSendReply}
              sending={sending}
              markingSession={markingSession}
              actionError={actionError}
              visitorTyping={visitorTyping}
              visitorTypingLabel={`${sessionLabel(selectedSession).split(' (')[0]} is typing…`}
              quickReplies={quickReplies}
              onQuickReply={onQuickReply}
              currentStatus={selectedSession.status}
              onMarkAs={onMarkSession}
            />

            {!canReply && actionError ? (
              <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
                <Alert severity="error">{actionError}</Alert>
              </Box>
            ) : null}
          </Box>
        </>
      )}
    </Paper>
  );
}
